import React, { useState, useEffect, useCallback } from 'react';
import Card from '../components/ui/Card';
import Button from '../components/ui/Button';
import Badge from '../components/ui/Badge';
import Modal from '../components/ui/Modal';
import Alert from '../components/ui/Alert';

const API_BASE = '/auth';

function getToken() {
  return localStorage.getItem('mickey_token');
}

// Keep this wrapper local because the Admin Panel's /auth routes sit outside
// the /api prefix added by the shared API helper.
async function authFetch(url, options = {}) {
  const res = await fetch(url, {
    ...options,
    headers: {
      'Content-Type': 'application/json',
      'Authorization': `Bearer ${getToken()}`,
      ...(options.headers || {}),
    },
  });
  if (res.status === 401) {
    localStorage.removeItem('mickey_token');
    window.location.href = '/login';
    // Marked like the shared helper's 401, so each caller below shows no error
    // words for the instant before the browser leaves for sign-in.
    const err = new Error('Unauthorized');
    err.status = 401;
    throw err;
  }
  return res;
}

export default function AdminPanel() {
  const [tab, setTab] = useState('users');

  const tabs = [
    { id: 'users', label: 'Users' },
    { id: 'permissions', label: 'Module Permissions' },
    { id: 'audit', label: 'Audit Log' },
    { id: 'branches', label: 'Branches' },
  ];

  return (
    <div className="max-w-5xl">
      <h1 className="text-2xl font-bold text-gray-900 dark:text-gray-100 mb-6">Admin Panel</h1>

      <div className="flex border-b border-gray-200 dark:border-slate-700 mb-6">
        {tabs.map(t => (
          <button
            key={t.id}
            onClick={() => setTab(t.id)}
            className={`px-4 py-2.5 text-sm font-medium border-b-2 -mb-px transition-colors ${
              tab === t.id
                ? 'text-primary border-primary'
                : 'text-gray-500 dark:text-gray-400 border-transparent hover:text-gray-700 dark:hover:text-gray-300'
            }`}
          >
            {t.label}
          </button>
        ))}
      </div>

      {tab === 'users' && <UserManagement />}
      {tab === 'permissions' && <PermissionMatrix />}
      {tab === 'audit' && <AuditLog />}
      {tab === 'branches' && <BranchManagement />}
    </div>
  );
}

/* ── User Management ─────────────────────────────── */

function UserManagement() {
  const [users, setUsers] = useState([]);
  const [roles, setRoles] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editingUser, setEditingUser] = useState(null);
  const [showCreateForm, setShowCreateForm] = useState(false);
  const [error, setError] = useState('');

  const loadData = useCallback(async () => {
    setLoading(true);
    setError('');
    try {
      const [usersRes, rolesRes] = await Promise.all([
        authFetch(`${API_BASE}/users`),
        authFetch(`${API_BASE}/roles`),
      ]);

      if (!usersRes.ok || !rolesRes.ok) {
        setError(!usersRes.ok ? `Failed to load users (${usersRes.status})` : `Failed to load roles (${rolesRes.status})`);
        return;
      }

      const usersData = await usersRes.json();
      const rolesData = await rolesRes.json();
      setUsers(Array.isArray(usersData) ? usersData : []);
      setRoles(Array.isArray(rolesData) ? rolesData : []);
    } catch (e) {
      if (e.status !== 401) setError('Network error loading users');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { loadData(); }, [loadData]);

  if (loading) {
    return (
      <div className="flex items-center justify-center py-12 text-gray-500 dark:text-gray-400">
        <div className="w-6 h-6 border-2 border-gray-200 dark:border-slate-600 border-t-primary rounded-full animate-spin mr-3" />
        Loading users...
      </div>
    );
  }

  return (
    <div>
      {error && <Alert variant="error" className="mb-4">{error}</Alert>}

      <div className="flex items-center justify-between mb-4">
        <h2 className="text-lg font-semibold text-gray-900 dark:text-gray-100">Users ({users.length})</h2>
        <Button onClick={() => setShowCreateForm(true)}>+ Add User</Button>
      </div>

      {showCreateForm && (
        <CreateUserForm
          roles={roles}
          onCreated={() => { setShowCreateForm(false); loadData(); }}
          onCancel={() => setShowCreateForm(false)}
        />
      )}

      <Card className="overflow-x-auto">
        <table className="w-full text-sm">
          <thead>
            <tr className="border-b border-gray-200 dark:border-slate-700 bg-gray-50 dark:bg-slate-800/50">
              <Th>Name</Th><Th>Email</Th><Th>Job Title</Th><Th>Roles</Th><Th>Status</Th><Th>Last Login</Th><Th>Actions</Th>
            </tr>
          </thead>
          <tbody className="divide-y divide-gray-100 dark:divide-slate-700">
            {users.map(user => (
              <tr key={user.id} className={`hover:bg-gray-50 dark:hover:bg-slate-800/50 ${!user.is_active ? 'opacity-50' : ''}`}>
                <td className="px-4 py-3 font-medium">{user.display_name || '—'}</td>
                <td className="px-4 py-3">{user.email}</td>
                <td className="px-4 py-3 text-gray-500 dark:text-gray-400">{user.title || '—'}</td>
                <td className="px-4 py-3">
                  <div className="flex flex-wrap gap-1">
                    {user.roles?.map(r => (
                      <Badge key={r.id} variant={r.name === 'Admin' ? 'warning' : 'primary'}>{r.name}</Badge>
                    ))}
                    {(!user.roles || user.roles.length === 0) && (
                      <span className="text-xs text-gray-400 italic">No role</span>
                    )}
                  </div>
                </td>
                <td className="px-4 py-3">
                  <span className={`inline-flex items-center gap-1.5 text-xs font-medium ${user.is_active ? 'text-success' : 'text-gray-400'}`}>
                    <span className={`w-2 h-2 rounded-full ${user.is_active ? 'bg-success' : 'bg-gray-300 dark:bg-gray-600'}`} />
                    {user.is_active ? 'Active' : 'Inactive'}
                  </span>
                </td>
                <td className="px-4 py-3 text-gray-500 dark:text-gray-400">{user.last_login ? new Date(user.last_login).toLocaleDateString() : 'Never'}</td>
                <td className="px-4 py-3">
                  <Button variant="ghost" size="sm" onClick={() => setEditingUser(user)}>Edit</Button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </Card>

      {editingUser && (
        <EditUserModal
          user={editingUser}
          roles={roles}
          onSaved={() => { setEditingUser(null); loadData(); }}
          onClose={() => setEditingUser(null)}
        />
      )}
    </div>
  );
}


// ── Branch / Office Management ──
function BranchManagement() {
  const [branches, setBranches] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editing, setEditing] = useState(null);
  const [newBranch, setNewBranch] = useState({ city: '', state: '', address: '' });
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');

  useEffect(() => { loadBranches(); }, []);

  async function loadBranches() {
    setError('');
    setLoading(true);
    try {
      const res = await authFetch('/api/tenant/branches');
      if (!res.ok) {
        setError(`Failed to load branches (${res.status})`);
        return;
      }
      const data = await res.json();
      setBranches(Array.isArray(data?.branches) ? data.branches : []);
    } catch (e) {
      if (e.status !== 401) setError('Network error loading branches');
    } finally {
      setLoading(false);
    }
  }

  async function saveBranches(updated) {
    setError('');
    setSaving(true);
    try {
      const res = await authFetch('/api/tenant/branches', {
        method: 'PUT',
        body: JSON.stringify({ branches: updated }),
      });
      // A refused save leaves the table as it was
      // and tells the user that nothing was saved.
      if (!res.ok) {
        setError(`Failed to save branches (${res.status}). Nothing was saved.`);
        return false;
      }
      setBranches(updated);
      return true;
    } catch (e) {
      if (e.status !== 401) setError('Network error saving branches. Nothing was saved.');
      return false;
    } finally {
      setSaving(false);
    }
  }

  async function addBranch() {
    if (!newBranch.city || !newBranch.state) return;
    const updated = [...branches, { ...newBranch }];
    if (await saveBranches(updated)) setNewBranch({ city: '', state: '', address: '' });
  }

  function removeBranch(idx) {
    const updated = branches.filter((_, i) => i !== idx);
    saveBranches(updated);
  }

  function updateBranch(idx, field, value) {
    const updated = branches.map((b, i) => i === idx ? { ...b, [field]: value } : b);
    setBranches(updated);
  }

  async function saveEdit() {
    if (await saveBranches(branches)) setEditing(null);
  }

  if (loading) return <Card className="p-6 text-center text-gray-400">Loading...</Card>;

  return (
    <div className="space-y-4">
      {error && <Alert variant="error">{error}</Alert>}
      <Card className="overflow-x-auto">
        <table className="w-full text-sm">
          <thead>
            <tr className="border-b border-gray-200 dark:border-slate-700 bg-gray-50 dark:bg-slate-800/50">
              <Th>City</Th><Th>State</Th><Th>Address</Th><Th>Actions</Th>
            </tr>
          </thead>
          <tbody className="divide-y divide-gray-100 dark:divide-slate-700">
            {branches.map((b, i) => (
              <tr key={i}>
                {editing === i ? (
                  <>
                    <td className="px-4 py-2"><input value={b.city} onChange={e => updateBranch(i, 'city', e.target.value)} className="w-full px-2 py-1 text-sm border border-gray-300 dark:border-slate-600 rounded bg-white dark:bg-slate-700 text-gray-900 dark:text-gray-100" /></td>
                    <td className="px-4 py-2"><input value={b.state} onChange={e => updateBranch(i, 'state', e.target.value)} className="w-20 px-2 py-1 text-sm border border-gray-300 dark:border-slate-600 rounded bg-white dark:bg-slate-700 text-gray-900 dark:text-gray-100" /></td>
                    <td className="px-4 py-2"><input value={b.address || ''} onChange={e => updateBranch(i, 'address', e.target.value)} className="w-full px-2 py-1 text-sm border border-gray-300 dark:border-slate-600 rounded bg-white dark:bg-slate-700 text-gray-900 dark:text-gray-100" /></td>
                    <td className="px-4 py-2 flex gap-2">
                      <Button variant="primary" size="sm" onClick={saveEdit}>Save</Button>
                      <Button variant="ghost" size="sm" onClick={() => { setEditing(null); loadBranches(); }}>Cancel</Button>
                    </td>
                  </>
                ) : (
                  <>
                    <td className="px-4 py-3 font-medium">{b.city}</td>
                    <td className="px-4 py-3">{b.state}</td>
                    <td className="px-4 py-3 text-gray-500 dark:text-gray-400">{b.address || '—'}</td>
                    <td className="px-4 py-3 flex gap-2">
                      <Button variant="ghost" size="sm" onClick={() => setEditing(i)}>Edit</Button>
                      <Button variant="ghost" size="sm" onClick={() => removeBranch(i)} className="text-red-500">Remove</Button>
                    </td>
                  </>
                )}
              </tr>
            ))}
          </tbody>
        </table>
      </Card>

      <Card className="p-4">
        <h3 className="text-sm font-bold text-gray-900 dark:text-gray-100 mb-3">Add Branch</h3>
        <div className="flex gap-3 items-end">
          <div>
            <label className="text-xs text-gray-500 block mb-1">City</label>
            <input value={newBranch.city} onChange={e => setNewBranch(p => ({ ...p, city: e.target.value }))} placeholder="City" className="px-3 py-2 text-sm border border-gray-300 dark:border-slate-600 rounded-lg bg-white dark:bg-slate-700 text-gray-900 dark:text-gray-100" />
          </div>
          <div>
            <label className="text-xs text-gray-500 block mb-1">State</label>
            <input value={newBranch.state} onChange={e => setNewBranch(p => ({ ...p, state: e.target.value }))} placeholder="ST" className="w-16 px-3 py-2 text-sm border border-gray-300 dark:border-slate-600 rounded-lg bg-white dark:bg-slate-700 text-gray-900 dark:text-gray-100" />
          </div>
          <div className="flex-1">
            <label className="text-xs text-gray-500 block mb-1">Address</label>
            <input value={newBranch.address} onChange={e => setNewBranch(p => ({ ...p, address: e.target.value }))} placeholder="Full address" className="w-full px-3 py-2 text-sm border border-gray-300 dark:border-slate-600 rounded-lg bg-white dark:bg-slate-700 text-gray-900 dark:text-gray-100" />
          </div>
          <Button onClick={addBranch} disabled={!newBranch.city || !newBranch.state || saving}>Add</Button>
        </div>
      </Card>
    </div>
  );
}

function Th({ children }) {
  return <th className="text-left px-4 py-3 text-xs font-semibold uppercase tracking-wide text-gray-500 dark:text-gray-400">{children}</th>;
}

/* ── Create User Form ────────────────────────────── */

function CreateUserForm({ roles, onCreated, onCancel }) {
  const [form, setForm] = useState({ email: '', displayName: '', title: '', password: '', roleIds: [] });
  const [error, setError] = useState('');
  const [submitting, setSubmitting] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setSubmitting(true);

    try {
      const res = await authFetch(`${API_BASE}/users`, {
        method: 'POST',
        body: JSON.stringify(form),
      });

      if (!res.ok) {
        const data = await res.json();
        setError(data.error || 'Failed to create user');
        return;
      }
      onCreated();
    } catch (e) {
      if (e.status !== 401) setError('Network error');
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Card className="p-5 mb-6">
      <form onSubmit={handleSubmit}>
        <h3 className="text-base font-semibold text-gray-900 dark:text-gray-100 mb-4">Create User</h3>
        {error && <Alert variant="error" className="mb-4">{error}</Alert>}

        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 mb-4">
          <FormField label="Email *">
            <input type="email" required value={form.email} onChange={e => setForm({ ...form, email: e.target.value })} className={inputCls} />
          </FormField>
          <FormField label="Display Name">
            <input type="text" value={form.displayName} onChange={e => setForm({ ...form, displayName: e.target.value })} className={inputCls} />
          </FormField>
          <FormField label="Title">
            <input type="text" value={form.title} placeholder="e.g. Project Manager" onChange={e => setForm({ ...form, title: e.target.value })} className={inputCls} />
          </FormField>
          <FormField label="Password *">
            <input type="password" required minLength={8} value={form.password} onChange={e => setForm({ ...form, password: e.target.value })} className={inputCls} />
          </FormField>
        </div>

        <fieldset className="border border-gray-200 dark:border-slate-700 rounded-md p-4 mb-4">
          <legend className="text-sm font-medium px-2">Roles</legend>
          <div className="flex flex-wrap gap-3">
            {roles.map(role => (
              <label key={role.id} className="flex items-center gap-2 text-sm cursor-pointer">
                <input
                  type="checkbox"
                  checked={form.roleIds.includes(role.id)}
                  onChange={(e) => setForm({
                    ...form,
                    roleIds: e.target.checked ? [...form.roleIds, role.id] : form.roleIds.filter(id => id !== role.id),
                  })}
                  className="rounded border-gray-300 text-primary focus:ring-primary"
                />
                {role.name}
              </label>
            ))}
          </div>
        </fieldset>

        <div className="flex gap-3 justify-end">
          <Button type="submit" disabled={submitting}>{submitting ? 'Creating...' : 'Create User'}</Button>
          <Button type="button" variant="secondary" onClick={onCancel}>Cancel</Button>
        </div>
      </form>
    </Card>
  );
}

/* ── Edit User Modal ─────────────────────────────── */

function EditUserModal({ user, roles, onSaved, onClose }) {
  const [form, setForm] = useState({
    displayName: user.display_name || '',
    title: user.title || '',
    isActive: user.is_active,
    primaryRole: (() => {
      // Find the non-Admin role, or default to the first role
      const nonAdmin = user.roles?.find(r => r.name !== 'Admin');
      return nonAdmin?.id || user.roles?.[0]?.id || '';
    })(),
    isAdmin: user.roles?.some(r => r.name === 'Admin') || false,
  });
  const [error, setError] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [resetSent, setResetSent] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setSubmitting(true);

    try {
      // Update profile
      const profileRes = await authFetch(API_BASE + '/users/' + user.id, {
        method: 'PUT',
        body: JSON.stringify({
          displayName: form.displayName,
          title: form.title,
          isActive: form.isActive,
        }),
      });

      if (!profileRes.ok) {
        const data = await profileRes.json();
        setError(data.error || 'Failed to update user');
        return;
      }

      // Build role IDs: primary role + admin if toggled
      const roleIds = [];
      if (form.primaryRole) roleIds.push(form.primaryRole);
      if (form.isAdmin) {
        const adminRole = roles.find(r => r.name === 'Admin');
        if (adminRole && !roleIds.includes(adminRole.id)) roleIds.push(adminRole.id);
      }

      const rolesRes = await authFetch(API_BASE + '/users/' + user.id + '/roles', {
        method: 'PUT',
        body: JSON.stringify({ roleIds }),
      });

      if (!rolesRes.ok) {
        const data = await rolesRes.json();
        setError(data.error || 'Failed to update roles');
        return;
      }

      onSaved();
    } catch (e) {
      if (e.status !== 401) setError('Network error');
    } finally {
      setSubmitting(false);
    }
  };

  async function sendPasswordReset() {
    try {
      const res = await authFetch(API_BASE + '/reset-password', {
        method: 'POST',
        body: JSON.stringify({ email: user.email }),
      });
      if (res.ok) {
        setResetSent(true);
      } else {
        setError('Failed to send password reset');
      }
    } catch (e) {
      if (e.status !== 401) setError('Failed to send password reset');
    }
  }

  const nonAdminRoles = roles.filter(r => r.name !== 'Admin');

  return (
    <Modal onClose={onClose} title={'Edit User: ' + user.email}>
      <form onSubmit={handleSubmit}>
        {error && <Alert variant="error" className="mb-4">{error}</Alert>}

        <div className="space-y-4 mb-4">
          <FormField label="Display Name">
            <input type="text" value={form.displayName} onChange={e => setForm({ ...form, displayName: e.target.value })} className={inputCls} />
          </FormField>
          <FormField label="Title">
            <input type="text" value={form.title} onChange={e => setForm({ ...form, title: e.target.value })} placeholder="e.g. Senior Project Manager" className={inputCls} />
          </FormField>

          <FormField label="Primary Role">
            <select value={form.primaryRole} onChange={e => setForm({ ...form, primaryRole: e.target.value })} className={inputCls}>
              <option value="">Select role...</option>
              {nonAdminRoles.map(role => (
                <option key={role.id} value={role.id}>{role.name}</option>
              ))}
            </select>
          </FormField>

          <div className="flex items-center gap-4">
            <label className="flex items-center gap-2 text-sm cursor-pointer">
              <input type="checkbox" checked={form.isAdmin} onChange={e => setForm({ ...form, isAdmin: e.target.checked })} className="rounded border-gray-300 text-primary focus:ring-primary" />
              Admin Access
            </label>
            <span className="text-xs text-gray-400">Grants full system access (user management, branches, settings)</span>
          </div>

          <label className="flex items-center gap-2 text-sm cursor-pointer">
            <input type="checkbox" checked={form.isActive} onChange={e => setForm({ ...form, isActive: e.target.checked })} className="rounded border-gray-300 text-primary focus:ring-primary" />
            Active
          </label>
        </div>

        {/* Password Reset */}
        <div className="border border-gray-200 dark:border-slate-700 rounded-md p-4 mb-4">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm font-medium text-gray-700 dark:text-gray-300">Password</p>
              <p className="text-xs text-gray-400">Send a password reset email to the user</p>
            </div>
            <Button type="button" variant="secondary" size="sm" onClick={sendPasswordReset} disabled={resetSent}>
              {resetSent ? 'Reset Email Sent ✓' : 'Send Password Reset'}
            </Button>
          </div>
        </div>

        <div className="flex gap-3 justify-end">
          <Button type="submit" disabled={submitting}>{submitting ? 'Saving...' : 'Save Changes'}</Button>
          <Button type="button" variant="secondary" onClick={onClose}>Cancel</Button>
        </div>
      </form>
    </Modal>
  );
}

/* ── Permission Matrix ───────────────────────────── */

function PermissionMatrix() {
  const [roles, setRoles] = useState([]);
  const [permissions, setPermissions] = useState([]);
  const [modules, setModules] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const [saveError, setSaveError] = useState('');

  useEffect(() => {
    (async () => {
      try {
        const [rolesRes, permsRes] = await Promise.all([
          authFetch(`${API_BASE}/roles`),
          authFetch(`${API_BASE}/permissions`),
        ]);
        if (!rolesRes.ok || !permsRes.ok) {
          setError(`Failed to load permissions (${!permsRes.ok ? permsRes.status : rolesRes.status})`);
          return;
        }
        const rolesData = await rolesRes.json();
        const permsData = await permsRes.json();
        const roleList = Array.isArray(rolesData) ? rolesData : [];
        const permList = Array.isArray(permsData) ? permsData : [];
        setRoles(roleList);
        setPermissions(permList);
        const mods = [...new Set(permList.map(p => p.module_name))].sort();
        setModules(mods);
      } catch (e) {
        if (e.status !== 401) setError('Network error loading permissions');
      } finally {
        setLoading(false);
      }
    })();
  }, []);

  const getPerm = (roleId, moduleName, level) => {
    const perm = permissions.find(p => p.role_id === roleId && p.module_name === moduleName);
    return perm?.[level] || false;
  };

  const togglePerm = (roleId, moduleName, level) => {
    const existing = permissions.find(p => p.role_id === roleId && p.module_name === moduleName);
    if (existing) {
      setPermissions(permissions.map(p =>
        p.role_id === roleId && p.module_name === moduleName ? { ...p, [level]: !p[level] } : p
      ));
    } else {
      setPermissions([...permissions, {
        role_id: roleId, module_name: moduleName,
        can_read: level === 'can_read', can_write: level === 'can_write', can_admin: level === 'can_admin',
      }]);
    }
  };

  const savePermissions = async () => {
    setSaving(true);
    setSaveError('');
    try {
      const res = await authFetch(`${API_BASE}/permissions`, {
        method: 'PUT',
        body: JSON.stringify({
          permissions: permissions.map(p => ({
            roleId: p.role_id, moduleName: p.module_name,
            canRead: !!p.can_read, canWrite: !!p.can_write, canAdmin: !!p.can_admin,
          })),
        }),
      });
      if (!res.ok) {
        setSaveError(`Failed to save permissions (${res.status}). Nothing was saved.`);
      }
    } catch (e) {
      if (e.status !== 401) setSaveError('Network error saving permissions.');
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center py-12 text-gray-500 dark:text-gray-400">
        <div className="w-6 h-6 border-2 border-gray-200 dark:border-slate-600 border-t-primary rounded-full animate-spin mr-3" />
        Loading permissions...
      </div>
    );
  }

  if (error) return <Alert variant="error">{error}</Alert>;

  return (
    <div>
      <div className="flex items-center justify-between mb-4">
        <h2 className="text-lg font-semibold text-gray-900 dark:text-gray-100">Module Permissions</h2>
        <Button onClick={savePermissions} disabled={saving}>{saving ? 'Saving...' : 'Save Changes'}</Button>
      </div>

      {saveError && <Alert variant="error" className="mb-4">{saveError}</Alert>}

      <Card className="overflow-x-auto">
        <table className="w-full text-sm">
          <thead>
            <tr className="border-b border-gray-200 dark:border-slate-700 bg-gray-50 dark:bg-slate-800/50">
              <Th>Module</Th>
              {roles.map(role => (
                <th key={role.id} colSpan={3} className="text-center px-2 py-3 text-xs font-semibold uppercase tracking-wide text-gray-500 dark:text-gray-400">
                  {role.name}
                </th>
              ))}
            </tr>
            <tr className="border-b border-gray-200 dark:border-slate-700">
              <th />
              {roles.map(role => (
                <React.Fragment key={role.id}>
                  <th className="text-center px-1 py-2 text-[10px] font-medium text-gray-400">R</th>
                  <th className="text-center px-1 py-2 text-[10px] font-medium text-gray-400">W</th>
                  <th className="text-center px-1 py-2 text-[10px] font-medium text-gray-400">A</th>
                </React.Fragment>
              ))}
            </tr>
          </thead>
          <tbody className="divide-y divide-gray-100 dark:divide-slate-700">
            {modules.map(mod => (
              <tr key={mod} className="hover:bg-gray-50 dark:hover:bg-slate-800/50">
                <td className="px-4 py-3 font-medium">{mod}</td>
                {roles.map(role => (
                  <React.Fragment key={role.id}>
                    {['can_read', 'can_write', 'can_admin'].map(level => (
                      <td key={level} className="text-center px-1 py-3">
                        <input
                          type="checkbox"
                          checked={getPerm(role.id, mod, level)}
                          onChange={() => togglePerm(role.id, mod, level)}
                          className="rounded border-gray-300 text-primary focus:ring-primary"
                        />
                      </td>
                    ))}
                  </React.Fragment>
                ))}
              </tr>
            ))}
          </tbody>
        </table>
      </Card>
    </div>
  );
}

/* ── Audit Log ───────────────────────────────────── */

function AuditLog() {
  const [logs, setLogs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  useEffect(() => {
    (async () => {
      try {
        const res = await authFetch(`${API_BASE}/audit?limit=100`);
        if (!res.ok) {
          setError(`Failed to load audit log (${res.status})`);
          return;
        }
        const data = await res.json();
        setLogs(Array.isArray(data) ? data : []);
      } catch (e) {
        if (e.status !== 401) setError('Network error loading audit log');
      } finally {
        setLoading(false);
      }
    })();
  }, []);

  if (loading) {
    return (
      <div className="flex items-center justify-center py-12 text-gray-500 dark:text-gray-400">
        <div className="w-6 h-6 border-2 border-gray-200 dark:border-slate-600 border-t-primary rounded-full animate-spin mr-3" />
        Loading audit log...
      </div>
    );
  }

  if (error) return <Alert variant="error">{error}</Alert>;

  return (
    <div>
      <h2 className="text-lg font-semibold text-gray-900 dark:text-gray-100 mb-4">Audit Log</h2>
      <Card className="overflow-x-auto">
        <table className="w-full text-sm">
          <thead>
            <tr className="border-b border-gray-200 dark:border-slate-700 bg-gray-50 dark:bg-slate-800/50">
              <Th>Time</Th><Th>Action</Th><Th>Details</Th>
            </tr>
          </thead>
          <tbody className="divide-y divide-gray-100 dark:divide-slate-700">
            {logs.map(log => (
              <tr key={log.id} className="hover:bg-gray-50 dark:hover:bg-slate-800/50">
                <td className="px-4 py-3 whitespace-nowrap text-gray-500 dark:text-gray-400">{new Date(log.created_at).toLocaleString()}</td>
                <td className="px-4 py-3">
                  <span className="inline-block px-2 py-0.5 bg-gray-100 dark:bg-slate-700 rounded text-xs font-mono">{log.action}</span>
                </td>
                <td className="px-4 py-3 text-xs text-gray-500 dark:text-gray-400">
                  {log.target_user_id && <span>User: {log.target_user_id.slice(0, 8)}...</span>}
                  {log.details && Object.keys(log.details).length > 0 && (
                    <code className="ml-2 text-[10px] bg-gray-100 dark:bg-slate-700 px-1.5 py-0.5 rounded max-w-[300px] truncate inline-block align-middle">
                      {JSON.stringify(log.details)}
                    </code>
                  )}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </Card>
    </div>
  );
}

/* ── Shared Helpers ──────────────────────────────── */

const inputCls = 'w-full px-3 py-2 border border-gray-200 dark:border-slate-600 rounded-md text-sm bg-white dark:bg-slate-800 text-gray-900 dark:text-gray-100 focus:outline-none focus:border-primary focus:ring-2 focus:ring-primary/10';

function FormField({ label, children }) {
  return (
    <div>
      <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">{label}</label>
      {children}
    </div>
  );
}
